import { Controller } from "@hotwired/stimulus"

/**
 * Mobile Carousel Controller
 * Simplified for mobile: Shows one slide at a time, perfectly centered
 */
export default class extends Controller {
  static targets = ["slide", "container"]
  
  connect() {
    // Initialize variables
    this.currentPage = 1
    this.slideWidth = 180 // Each testimonial slide is 180px wide
    this.slideMargin = 6 // 3px margin on each side (mx-3 = 6px total)
    this.slidesPerPage = 1 // Mobile always shows 1 slide
    this.slidesToMove = 1 
    this.totalPages = Math.max(0, this.slideTargets.length - this.slidesPerPage) + 1
    
    // Initialize drag variables
    this.isDragging = false
    this.startPos = 0
    this.currentTranslate = 0
    this.prevTranslate = 0
    this.dragThreshold = 50 // Minimum drag distance to trigger slide change
    
    // Set up event listeners
    this.setupEventListeners()
    
    // Initial positioning with slight delay to ensure DOM is ready
    setTimeout(() => {
      this.updateView(true)
    }, 100)
  }
  
  setupEventListeners() {
    // Mobile touch events for swiping
    this.containerTarget.addEventListener('touchstart', this.touchStart.bind(this), { passive: true })
    this.containerTarget.addEventListener('touchmove', this.touchMove.bind(this), { passive: false })
    this.containerTarget.addEventListener('touchend', this.touchEnd.bind(this), { passive: true })
    
    // Mouse events for dragging (desktop fallback)
    this.containerTarget.addEventListener('mousedown', this.touchStart.bind(this))
    window.addEventListener('mousemove', this.touchMove.bind(this))
    window.addEventListener('mouseup', this.touchEnd.bind(this))
    
    // Window resize event
    window.addEventListener('resize', this.handleResize.bind(this))
  }
  
  handleResize() {
    // Update view when resizing - always stays at 1 slide for mobile
    this.updateView(true)
  }
  
  // Navigation methods
  next() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++
      this.updateView()
    }
  }
  
  prev() {
    if (this.currentPage > 1) {
      this.currentPage--
      this.updateView()
    }
  }
    // Mobile-optimized update view method
  updateView(force = false) {
    const slideWidth = this.slideWidth
    const slideMargin = this.slideMargin
    const slideUnitWidth = slideWidth + slideMargin
    
    // Get container width
    const containerWidth = this.containerTarget.parentElement.offsetWidth
    
    // Full-width approach - center current slide
    // Get current slide index (0-based)
    const slideIndex = this.currentPage - 1
    
    // Calculate viewport width (actual visible area)
    const viewportWidth = containerWidth
    
    // Calculate center position for current slide
    const centerOffset = (viewportWidth - slideWidth) / 2
    
    // Calculate translateX to center current slide in viewport
    const translateX = centerOffset - (slideIndex * slideUnitWidth)
    
    console.log('Mobile view:', { 
      viewportWidth,
      slideWidth,
      slideIndex,
      centerOffset,
      translateX
    })
    
    // Enable transition for animation unless we're in a drag operation
    if (!this.isDragging) {
      this.containerTarget.style.transition = force ? 'transform 300ms ease' : 'none'
    }
    
    // Apply transform to move slides
    this.containerTarget.style.transform = `translateX(${translateX}px)`
    
    // Set explicit width to container to hold all slides properly
    // For mobile, we set proper width for all slides while keeping it simple
    const totalContentWidth = this.slideTargets.length * slideUnitWidth
    this.containerTarget.style.width = `${totalContentWidth}px`
    
    // Update slides visibility for better performance
    this.updateSlidesVisibility(slideIndex)
  }
  
  // Touch and drag handling
  touchStart(e) {
    this.isDragging = true
    this.startPos = this.getPositionX(e)
    
    // Store previous transform value
    const style = window.getComputedStyle(this.containerTarget)
    const transform = style.getPropertyValue('transform')
    const matrix = new DOMMatrixReadOnly(transform)
    this.prevTranslate = matrix.m41 // Get the X translation component
    
    // Disable transition during dragging for responsive feel
    this.containerTarget.style.transition = 'none'
  }
  
  touchMove(e) {
    if (!this.isDragging) return
    
    const currentPosition = this.getPositionX(e)
    const diff = currentPosition - this.startPos
    
    // Prevent scrolling the page while dragging
    e.preventDefault()
    
    // Calculate how much to move
    this.currentTranslate = this.prevTranslate + diff
    
    // Apply the movement with transform - for full width
    this.containerTarget.style.transform = `translateX(${this.currentTranslate}px)`
  }
  
  touchEnd(e) {
    if (!this.isDragging) return
    this.isDragging = false
    
    const currentPosition = this.getPositionX(e)
    const diff = currentPosition - this.startPos
    
    // Re-enable transitions for smooth snapping
    this.containerTarget.style.transition = 'transform 300ms ease'
    
    // If dragged far enough, change slide
    if (Math.abs(diff) > this.dragThreshold) {
      if (diff > 0 && this.currentPage > 1) {
        // Previous slide
        this.currentPage--
        this.updateView(true)
      } else if (diff < 0 && this.currentPage < this.totalPages) {
        // Next slide
        this.currentPage++
        this.updateView(true)
      } else {
        // Edge case - can't go past first or last slide
        this.updateView(true)
      }
    } else {
      // Not dragged far enough, snap back to current slide
      this.updateView(true)
    }
  }
  
  getPositionX(e) {
    return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX
  }
  
  // Add a method to update slides visibility for better performance
  updateSlidesVisibility(currentIndex) {
    // For full width carousel, we'll show the current slide and adjacent ones
    const visibleRange = 2 // Show current slide, plus 2 on each side
    
    this.slideTargets.forEach((slide, index) => {
      const distance = Math.abs(index - currentIndex)
      
      if (distance <= visibleRange) {
        // Slide is within visible range
        slide.style.opacity = 1 - (distance * 0.15) // Slight fade for non-active slides
        slide.style.visibility = 'visible'
        slide.style.pointerEvents = 'auto'
      } else {
        // Slide is outside visible range
        slide.style.opacity = 0.3
        slide.style.visibility = 'visible' // Keep visible for full-width effect
        slide.style.pointerEvents = 'none'
      }
    })
  }
}
